import type { EventBus } from '../../core/EventBus';
import type { Store } from '../../core/Store';
import { buildVideoStateMetrics } from './videoStateMetrics';

interface BindVideoEventsParams {
  video: HTMLVideoElement;
  eventBus: EventBus;
  store: Store;
  getSource: () => string | undefined;
  getProgrammaticVolumeChange: () => number;
  setProgrammaticVolumeChange: (value: number) => void;
  getMutedStateOverride?: () => boolean | null;
  suppressLoadingState?: () => boolean;
  onPlaying?: () => boolean | void;
}

export function bindVideoEvents({
  video,
  eventBus,
  store,
  getSource,
  getProgrammaticVolumeChange,
  setProgrammaticVolumeChange,
  getMutedStateOverride,
  suppressLoadingState,
  onPlaying,
}: BindVideoEventsParams): void {
  video.addEventListener('playing', () => {
    const keepLoading = onPlaying?.() === true;
    if (suppressLoadingState?.()) {
      store.setState({ isPlaying: true });
    } else {
      store.setState({ isPlaying: true, loading: keepLoading });
    }
    eventBus.emit('playing');
  });

  video.addEventListener('pause', () => {
    store.setState({ isPlaying: false });
  });

  video.addEventListener('waiting', () => {
    if (!suppressLoadingState?.()) {
      store.setState({ loading: true });
    }
    eventBus.emit('video:waiting', buildVideoStateMetrics(video, 'waiting', getSource(), store.getState().quality));
  });

  video.addEventListener('seeking', () => eventBus.emit('video:seeking'));

  video.addEventListener('timeupdate', () => {
    store.setState({ currentTime: video.currentTime });
    eventBus.emit('time:update', video.currentTime);
  });

  video.addEventListener('loadedmetadata', () => {
    const dur = isFinite(video.duration) ? video.duration : 0;
    store.setState({ duration: dur });
  });

  video.addEventListener('durationchange', () => {
    const dur = isFinite(video.duration) ? video.duration : 0;
    store.setState({ duration: dur });
  });

  video.addEventListener('ended', () => {
    store.setState({ isPlaying: false });
    eventBus.emit('ended');
  });

  video.addEventListener('stalled', () => {
    eventBus.emit('video:stalled', buildVideoStateMetrics(video, 'stalled', getSource(), store.getState().quality));
  });

  video.addEventListener('error', () => {
    const e = video.error;
    eventBus.emit('error', {
      code: e?.code ?? 0,
      message: e?.message ?? 'Unknown video error',
      fatal: true,
    });
  });

  let lastMuted = video.muted;
  video.addEventListener('volumechange', () => {
    const vol = Math.round(video.volume * 100) / 100;
    const programmaticVolumeChange = getProgrammaticVolumeChange();
    if (programmaticVolumeChange > 0) {
      // Programmatic change — update store only, do not re-emit events
      setProgrammaticVolumeChange(programmaticVolumeChange - 1);
      const mutedOverride = getMutedStateOverride?.();
      store.setState({ volume: vol, isMuted: mutedOverride ?? (video.muted || vol === 0) });
      lastMuted = video.muted;
      return;
    }
    // Native/unsolicited change (right-click mute, OS media keys)
    store.setState({ volume: vol, isMuted: video.muted || vol === 0 });
    eventBus.emit('volume:change', vol);
    if (video.muted !== lastMuted) {
      lastMuted = video.muted;
      eventBus.emit('muted:change', video.muted);
    }
  });
}
